import { useEffect } from 'react'
import type { RefObject } from 'react'
import { useModalStore } from '@/hooks/useModalStore'
import { useViewportStore, SCALE_MIN, SCALE_MAX } from '@/stores'

/**
 * Attaches a non-passive wheel listener to the container element and performs
 * cursor-centred zoom.
 *
 * The canvas transform is `scale(sc) translate(tx, ty)` with transformOrigin 0 0.
 * A canvas point (cx, cy) maps to screen (sx, sy) as:
 *   sx = sc * (cx + tx)
 *   sy = sc * (cy + ty)
 *
 * To keep the canvas point under the cursor fixed while zooming:
 *   newTx = sx/newSc - cx = sx*(1/newSc - 1/sc) + tx
 * where sx = cursorX_relative_to_container.
 */
export function useZoom(containerRef: RefObject<HTMLElement>): void {
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()

      const modalState = useModalStore.getState()
      if (modalState.fkPanel.open) modalState.closeFkPanel()
      if (modalState.indexPanel.open) modalState.closeIndexPanel()
      if (modalState.relationPopup.open) modalState.closeRelationPopup()

      const { sc, tx, ty, setViewport } = useViewportStore.getState()
      const rect = el.getBoundingClientRect()

      // Additive delta matching legacy behaviour (±0.08)
      const delta = e.deltaY < 0 ? 0.08 : -0.08
      const newSc  = Math.min(SCALE_MAX, Math.max(SCALE_MIN, sc + delta))

      // Cursor position relative to the canvas container
      const px = e.clientX - rect.left
      const py = e.clientY - rect.top

      // Keep the canvas point under the cursor at the same screen position
      const factor = 1 / newSc - 1 / sc
      const newTx  = px * factor + tx
      const newTy  = py * factor + ty

      setViewport(newSc, newTx, newTy)
    }

    el.addEventListener('wheel', handleWheel, { passive: false })
    return () => el.removeEventListener('wheel', handleWheel)
  }, [containerRef])
}
